import React, { useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { httpsCallable } from "firebase/functions";
import { signInWithEmailAndPassword } from "firebase/auth";
import { auth, functions } from "../firebase";
import logo from "../assets/river-ranch-logo.png";
import hero from "../assets/hero-poker.jpg";

const COPPER = "#B87333";

function passwordMeetsMinimum(password) {
  return String(password || "").trim().length >= 8;
}

export default function ForcePasswordChange() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const completeTemporaryPasswordChange = useMemo(() => {
    return httpsCallable(functions, "completeTemporaryPasswordChange");
  }, []);

  const redirectTarget = useMemo(() => {
    const raw = searchParams.get("redirect");
    return raw ? decodeURIComponent(raw) : "/dashboard";
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    setErrorMessage("");
    setSuccessMessage("");

    const trimmedNew = String(newPassword || "").trim();
    const trimmedConfirm = String(confirmPassword || "").trim();

    if (!trimmedNew || !trimmedConfirm) {
      setErrorMessage("Please enter and confirm your new password.");
      return;
    }

    if (!passwordMeetsMinimum(trimmedNew)) {
      setErrorMessage("Password must be at least 8 characters.");
      return;
    }

    if (trimmedNew !== trimmedConfirm) {
      setErrorMessage("Passwords do not match.");
      return;
    }

    const currentEmail = String(auth.currentUser?.email || "").trim().toLowerCase();

    if (!currentEmail) {
      setErrorMessage(
        "Could not confirm your account email for re-login. Please log in again and retry."
      );
      return;
    }

    setSubmitting(true);

    try {
      await completeTemporaryPasswordChange({
        newPassword: trimmedNew,
        confirmPassword: trimmedConfirm,
      });

      await signInWithEmailAndPassword(auth, currentEmail, trimmedNew);

      try {
        await auth.currentUser?.reload?.();
        await auth.currentUser?.getIdToken?.(true);
      } catch (refreshError) {
        console.error("User refresh after password change failed:", refreshError);
      }

      setSuccessMessage("Password updated successfully. Redirecting...");

      setTimeout(() => {
        navigate(redirectTarget || "/dashboard", { replace: true });
      }, 700);
    } catch (error) {
      console.error("Forced password change failed:", error);

      setErrorMessage(
        error?.message ||
          "Unable to update password right now. Please try again."
      );
    }

    setSubmitting(false);
  };

  const handleLogout = async () => {
    try {
      await auth.signOut();
    } catch (error) {
      console.error("Logout failed:", error);
    }

    navigate("/login", { replace: true });
  };

  return (
    <div className="relative min-h-screen flex flex-col items-center justify-center bg-black text-white overflow-hidden px-6">
      <img
        src={hero}
        alt="Poker table"
        className="absolute inset-0 w-full h-full object-cover opacity-40"
      />

      <div className="absolute inset-0 bg-black/70" />

      <div className="absolute inset-0 opacity-30">
        <div className="absolute -top-40 -left-40 h-[520px] w-[520px] rounded-full blur-3xl bg-[#B87333]/20" />
        <div className="absolute -bottom-52 -right-52 h-[620px] w-[620px] rounded-full blur-3xl bg-[#B87333]/15" />
      </div>

      <form
        onSubmit={handleSubmit}
        className="relative z-10 bg-zinc-900/95 backdrop-blur-sm p-8 rounded-lg border border-zinc-800 w-full max-w-md space-y-4"
      >
        <div className="flex items-center justify-end">
          <button
            type="button"
            onClick={handleLogout}
            className="text-zinc-400 hover:text-zinc-200 text-sm"
          >
            Logout
          </button>
        </div>

        <img
          src={logo}
          alt="River Ranch Poker Club"
          className="h-34 mb-2 mx-auto"
        />

        <h2 className="text-2xl font-semibold text-center" style={{ color: COPPER }}>
          Change Temporary Password
        </h2>

        <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-4">
          <p className="text-sm text-amber-100 leading-relaxed">
            Your account is using a temporary password. You must choose a new
            password before continuing.
          </p>
        </div>

        <div className="space-y-2">
          <label className="block text-zinc-300 text-sm font-medium">
            New Password
          </label>
          <input
            type="password"
            placeholder="Enter new password"
            className="w-full p-3 bg-zinc-800 text-zinc-100 rounded"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            autoComplete="new-password"
            required
            disabled={submitting}
          />
        </div>

        <div className="space-y-2">
          <label className="block text-zinc-300 text-sm font-medium">
            Confirm New Password
          </label>
          <input
            type="password"
            placeholder="Confirm new password"
            className="w-full p-3 bg-zinc-800 text-zinc-100 rounded"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            autoComplete="new-password"
            required
            disabled={submitting}
          />
        </div>

        <p className="text-xs text-zinc-500">
          Use at least 8 characters. After saving, you’ll be redirected back into
          the app.
        </p>

        {successMessage ? (
          <div className="rounded-lg border border-green-500/30 bg-green-500/10 p-3">
            <p className="text-sm text-green-300">{successMessage}</p>
          </div>
        ) : null}

        {errorMessage ? (
          <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-3">
            <p className="text-sm text-red-300">{errorMessage}</p>
          </div>
        ) : null}

        <button
          type="submit"
          className="w-full py-3 bg-[#B87333] hover:bg-[#A46230] text-zinc-950 font-semibold rounded disabled:opacity-60"
          disabled={submitting}
        >
          {submitting ? "Updating Password..." : "Save New Password"}
        </button>
      </form>
    </div>
  );
}